import { useMemo } from "react";
import ModalShell from "../../../../_components/modal/ModalShell";
import ConfirmDialog from "../../../../_components/modal/ConfirmDialog";
import DynamicForm from "../../../../_components/form/DynamicForm";
import { useUsageCompilationFormState } from "./hooks/useUsageCompilationFormState";
import "./_usage-compilation-form-modal.scss";

/**
 * UI Layer - presentational composition only, mirrors
 * _features/usageSources/modals/UsageSourceFormModal/index.jsx's shape:
 * ModalShell + DynamicForm + ConfirmDialog. The "Calculation" section's
 * Reset button is NOT rendered here - it's wired via the schema's
 * `section.btn`, already handled generically by DynamicForm itself.
 */
const UsageCompilationFormModal = ({ isOpen, onClose, rowData, mode, onSuccess, refetchFilters }) => {
  const {
    control,
    schema,
    formData,
    errors,
    handleChange,
    handleBlur,
    showCloseConfirmation,
    isSubmitting,
    attemptClose,
    confirmDiscard,
    cancelDiscard,
    onSubmit,
  } = useUsageCompilationFormState({ mode, rowData, isOpen, onClose, onSuccess, refetchFilters });

  const description = useMemo(
    () =>
      mode === "edit"
        ? "Updates how this compilation calculates and maps usage."
        : "Defines how raw usage from a source rolls up into billable consumption.",
    [mode]
  );

  return (
    <>
      <ModalShell
        isOpen={isOpen}
        onRequestClose={attemptClose}
        width="460px"
        height="100vh"
        center={false}
        right={0}
        className="usage-compilation-form-modal--right-docked"
        title={mode === "edit" ? "Edit usage compilation" : "New usage compilation"}
        description={description}
        isSubmitting={isSubmitting}
        footerButtons={[
          { children: "Close", onClick: attemptClose, variant: "outlined" },
          {
            children: mode === "edit" ? "Update" : "Create",
            onClick: onSubmit,
            variant: "contained",
            loading: isSubmitting,
          },
        ]}
      >
        <form onSubmit={onSubmit} className="usage-compilation-form-modal__scroll">
          <DynamicForm
            schema={schema}
            control={control}
            formData={formData}
            errors={errors}
            handleChange={handleChange}
            handleBlur={handleBlur}
            mode={mode}
            disabled={isSubmitting}
          />
        </form>
      </ModalShell>

      <ConfirmDialog
        isOpen={showCloseConfirmation}
        title="Discard unsaved changes?"
        description="You have unsaved changes. Are you sure you want to close without saving?"
        cancelText="Keep editing"
        confirmText="Discard changes"
        onCancel={cancelDiscard}
        onConfirm={confirmDiscard}
      />
    </>
  );
};

export default UsageCompilationFormModal;
